import { translate, useDynamicData } from '@avensia/excite';
import { styled } from '@glitz/react';
import QuickSearch from 'webapp/search/QuickSearch';
import { SiteLogo } from 'webapp/shared/logos';
import * as React from 'react';
import { ButtonLink } from 'webapp/components/button/button';
import { ArrowLeft } from 'webapp/components/icons';
import { Block, desktopEdgeMarginDecorator, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import { isSvenssons, Theme } from 'webapp/theme';
import { addNavigationEventToDataLayer } from 'webapp/tracking/events/main-navigation';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import { ZIndex } from 'webapp/z-index';
import { useCurrentPageIsCheckoutPage } from 'webapp/pages/checkout/use-current-page-is-checkout-page';
import { CartButton } from 'webapp/shell/header/CartButton';
import { MyPagesButton } from 'webapp/shell/header/MyPagesButton';
import TopMenu from 'webapp/shell/header/TopMenu';
import { WishListButton as NNWishListButton } from 'nordicnest/shell/header/WishListButton';
import { WishListButton } from './WishListButton';
import Link from 'webapp/components/Link';
import { DESKTOP_HEADER_WITHOUT_MENU } from 'webapp/utils/constants';

export default function Header() {
  const isCheckoutPage = useCurrentPageIsCheckoutPage();
  const [startPageUrl] = useDynamicData<NordicNest.Api.WellknownPagesApiModel, string>({
    selector(data) {
      return data?.startPageUrl ?? '#';
    },
  });
  const topRef = React.useRef<HTMLAnchorElement>(null);

  //Setting focus on the top link when navigating to checkout page, needed for accessibility
  React.useEffect(() => {
    if (topRef.current && isCheckoutPage) {
      topRef.current.focus();
    }
  }, [isCheckoutPage]);

  return (
    <Base>
      <Container>
        <LeftContainer>
          {isCheckoutPage ? (
            <ButtonLink to={startPageUrl} as={Theme.Transparent} ref={topRef}>
              <ArrowLeft /> {translate(t => t.continueShopping)}
            </ButtonLink>
          ) : (
            <Link
              to={startPageUrl}
              aria-label={translate(t => t.header.toStartPage)}
              onClick={() =>
                addNavigationEventToDataLayer({ to: startPageUrl, menu_type: 'header', menu_category: 'logo' })
              }
            >
              <SiteLogo />
            </Link>
          )}
        </LeftContainer>
        {isCheckoutPage ? (
          <LogotypeLink
            to={startPageUrl}
            aria-label={translate(t => t.header.toStartPage)}
            onClick={() =>
              addNavigationEventToDataLayer({ to: startPageUrl, menu_type: 'header', menu_category: 'logo' })
            }
          >
            <SiteLogo />
          </LogotypeLink>
        ) : (
          <QuickSearch isLargeViewport />
        )}
        {!isCheckoutPage && (
          <Icons>
            <MyPagesButton />
            {isSvenssons ? <WishListButton /> : <NNWishListButton />}
            <CartButton />
          </Icons>
        )}
      </Container>
      {!isCheckoutPage && <TopMenu />}
    </Base>
  );
}

const Base = styled.header({
  flexDirection: 'column',
  alignItems: 'inherit',
  height: 'auto',
  position: 'sticky',
  backgroundColor: t => t.header?.backgroundColor,
  top: -1, // -1 to avoid inexplicable (?!) gap at the top
  zIndex: ZIndex.Header,
});

const Container = styled(
  Block,
  maxContentNormalWidthDecorator,
  desktopEdgeMarginDecorator,
  createMinLargeViewportDecorator({ height: DESKTOP_HEADER_WITHOUT_MENU }),
  {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
  },
);

const LeftContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifySelf: 'flex-start',
});

const LogotypeLink = styled(Link, {
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
});

const Icons = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifySelf: 'flex-end',
});
